import { createContext, useContext, type ReactNode } from 'react';
import type { MediaPart } from '@/model';
import { record } from './access';

export interface SharedMediaGalleryItem {
    readonly messageId: string;
    readonly seq: number;
    readonly partIndex: number;
    readonly senderId?: string;
    readonly createdAt?: string;
    readonly part: Readonly<MediaPart>;
}
/** Cached hints only. The shared gallery must establish visibility and range coverage itself. */
export interface SharedMediaGallerySeed {
    readonly conversationId: string;
    readonly selected: Readonly<{ messageId: string; seq: number; partIndex: number }>;
    readonly items: readonly SharedMediaGalleryItem[];
    current(): boolean;
}
const SeedContext = createContext<SharedMediaGallerySeed | undefined>(undefined);
export function SharedMediaGallerySeedProvider({ seed, children }: { seed?: SharedMediaGallerySeed; children: ReactNode }) { return <SeedContext.Provider value={seed}>{children}</SeedContext.Provider>; }
/** Additive public seam: Chat consumes cached Shared Media seeds without changing frozen viewer props. */
export function useSharedMediaGallerySeed(conversationId: string, messageId?: string, partIndex?: number): SharedMediaGallerySeed | undefined {
    const seed = useContext(SeedContext);
    return seed?.conversationId === conversationId && seed.current() && (messageId === undefined || seed.selected.messageId === messageId) && (partIndex === undefined || seed.selected.partIndex === partIndex) ? seed : undefined;
}
interface Candidate { message_id: string; seq: number; part_index: number; sender_id?: string; created_at?: string; part: unknown }
export function sharedMediaGallerySeed(conversationId: string, selected: Candidate, candidates: readonly Candidate[], current: () => boolean): SharedMediaGallerySeed | undefined {
    const tapped = record(selected.part);
    if (tapped.type !== 'media' || tapped.kind !== 'image') return;
    const items = new Map<string, SharedMediaGalleryItem>();
    for (const candidate of [...candidates, selected]) {
        const value = record(candidate.part);
        if (!candidate.message_id || !Number.isSafeInteger(candidate.seq) || candidate.seq <= 0 || !Number.isSafeInteger(candidate.part_index) || candidate.part_index < 0 || value.type !== 'media' || value.kind !== 'image' || typeof value.media_id !== 'string' || !value.media_id) continue;
        const part: MediaPart = { type: 'media', kind: 'image', media_id: value.media_id };
        if (typeof value.name === 'string') part.name = value.name;
        if (typeof value.mime === 'string') part.mime = value.mime;
        for (const field of ['bytes', 'width', 'height'] as const) if (typeof value[field] === 'number' && Number.isFinite(value[field]) && value[field] >= 0) part[field] = value[field];
        items.set(`${candidate.message_id}:${candidate.part_index}`, Object.freeze({ messageId: candidate.message_id, seq: candidate.seq, partIndex: candidate.part_index, senderId: candidate.sender_id, createdAt: candidate.created_at, part: Object.freeze(part) }));
    }
    if (!items.has(`${selected.message_id}:${selected.part_index}`)) return;
    return Object.freeze({ conversationId, selected: Object.freeze({ messageId: selected.message_id, seq: selected.seq, partIndex: selected.part_index }), items: Object.freeze([...items.values()].sort((a, b) => a.seq - b.seq || a.partIndex - b.partIndex)), current });
}
